import { Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import React, { type ErrorInfo, type PropsWithChildren } from 'react';

import { Text, Title } from '@notablehealth/design-system';

import { ReactComponent as ErrorIconSvg } from './error.svg';

interface Props extends PropsWithChildren {}

export class ErrorBoundary extends React.Component<Props> {
  state = { error: undefined };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (info.digest) {
      console.error(info.digest);
      Sentry.captureMessage(info.digest);
    }
    console.error(error);
    Sentry.captureException(error);
  }

  render() {
    if (this.state.error) {
      return (
        <Stack alignItems="center" gap={3} paddingY={20}>
          <ErrorIcon />
          <Title component="h1" variant="headline-large" bold>
            Oops! Something went wrong.
          </Title>
          <Text variant="paragraph-medium" bold>
            The page encountered an error, and we're on it. Please refresh or try again later.
            Thanks for your patience!
          </Text>
        </Stack>
      );
    }
    return this.props.children;
  }
}

const ErrorIcon = styled(ErrorIconSvg)`
  width: 154px;
  height: 154px;
`;
